// Create a consistent interface for subscription mutations
import { supabase } from '@/integrations/supabase/client';
import { Product, mapProductToDatabase } from '@/types/product';
import { getBillingPeriod } from '@/utils/billingPeriodUtils';

// Archive a subscription
export const archiveSubscription = async (id: string): Promise<boolean> => {
  try {
    // Update the status to 'archived'
    const { error } = await supabase
      .from('products')
      .update({ status: 'archived' })
      .eq('id', id)
      .neq('billing_period', 'one_time');
    
    if (error) {
      console.error(`Error archiving subscription ${id}:`, error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error(`Error in archiveSubscription for ${id}:`, error);
    return false;
  }
};

// Create a subscription (now creates a product record)
export const createSubscription = async (subscriptionData: Omit<Product, 'id' | 'created_at' | 'status'>): Promise<Product | null> => {
  try {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user || !user.id) {
      console.error('No authenticated user found when creating subscription');
      return null;
    }
    
    // Map to database fields
    const dbProduct = mapProductToDatabase({
      ...subscriptionData,
      id: crypto.randomUUID(),
      created_at: new Date().toISOString(),
      status: 'active',
      user_id: user.id
    });
    
    // Insert subscription as a product
    const { data, error } = await supabase
      .from('products')
      .insert(dbProduct)
      .select()
      .single();
    
    if (error) {
      console.error('Error creating subscription:', error);
      return null;
    }
    
    if (data) {
      // Convert database record to our Product type
      const createdProduct: Product = {
        id: data.id,
        title: data.name,
        name: data.name,
        description: data.description || '',
        amount: data.price_cents,
        price_cents: data.price_cents,
        status: 'active',
        created_at: data.created_at,
        billing_period: getBillingPeriod(data.billing_period),
        user_id: data.user_id
      };
      return createdProduct;
    }
    
    return null;
  } catch (error) {
    console.error('Error in createSubscription:', error);
    return null;
  }
};
